<script setup lang="ts">
import {
    computed, ref, watch,
} from 'vue';
import moment from 'moment';
import { BDateRangePicker, BChipGroup } from '@/design-system';
import type { GroupingPeriodType } from '@/types/GroupingPeriodType';
import type { DateRangeType } from '@/types/statistics/DateRangeType';
import type { DatePresetOptionType } from '@/types/DatePresetOptionType';
import { VDateInput } from 'vuetify/labs/VDateInput';
import { useTranslation } from '@/composables';

type PresetOption = DatePresetOptionType;

type PropsType = {
    label?: string;
    density?: VDateInput['$props']['density'];
    maxWidth?: string;
    hideLabel?: boolean;
    presets?: GroupingPeriodType[];

    /** Replaces the built-in rolling presets with the caller's own chips. */
    customPresets?: DatePresetOptionType[];

    /** Corner rounding of the date field, e.g. `xl` for a pill. */
    rounded?: string | boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    label: 'Date Range',
    density: 'compact',
    maxWidth: '300px',
    hideLabel: false,
    presets: () => ['day', 'week', 'month'],
});

const { t } = useTranslation();
const modelValue = defineModel<DateRangeType>();

const selectedPreset = ref<string | null>(null);

const presetOptions = computed((): PresetOption[] => {
    if (props.customPresets) {
        return props.customPresets;
    }

    const options: Record<GroupingPeriodType, PresetOption> = {
        day: {
            value: 'day',
            label: t('common.lastDay'),
            calculate: () => ({
                start: moment().subtract(1, 'days').startOf('day').toDate(),
                end: moment().endOf('day').toDate(),
            }),
        },
        week: {
            value: 'week',
            label: t('common.lastWeek'),
            calculate: () => ({
                start: moment().subtract(7, 'days').startOf('day').toDate(),
                end: moment().endOf('day').toDate(),
            }),
        },
        month: {
            value: 'month',
            label: t('common.lastMonth'),
            calculate: () => ({
                start: moment().subtract(1, 'month').startOf('day').toDate(),
                end: moment().endOf('day').toDate(),
            }),
        },
    };

    return props.presets.map(preset => options[preset]);
});

const chipItems = computed(() =>
    presetOptions.value.map(option => ({
        value: option.value,
        title: option.label,
    })),
);

const handlePresetChange = (value: string | number | (string | number)[] | null | undefined) => {
    if (!value || Array.isArray(value) || typeof value !== 'string') {
        selectedPreset.value = null;

        return;
    }

    const option = presetOptions.value.find(opt => opt.value === value);

    if (option) {
        selectedPreset.value = value;
        modelValue.value = option.calculate();
    }
};

const handleDateChange = (dateRange: DateRangeType | undefined) => {
    modelValue.value = dateRange;
    detectPresetFromDateRange(dateRange);
};

const detectPresetFromDateRange = (dateRange: DateRangeType | null | undefined) => {
    if (!dateRange) {
        selectedPreset.value = null;

        return;
    }

    const matchingPreset = presetOptions.value.find(option => {
        const presetRange = option.calculate();
        if (!presetRange) {
            return false;
        }

        const startMatches = moment(dateRange.start).isSame(moment(presetRange.start), 'day');
        const endMatches = moment(dateRange.end).isSame(moment(presetRange.end), 'day');

        return startMatches && endMatches;
    });

    selectedPreset.value = matchingPreset?.value || null;
};

watch(
    () => modelValue.value,
    newValue => {
        detectPresetFromDateRange(newValue);
    },
    { immediate: true },
);
</script>

<template>
    <div class="b-date-range-with-presets">
        <BDateRangePicker
            :model-value="modelValue"
            :label="label"
            :density="density"
            :hide-label="hideLabel"
            :rounded="rounded"
            prepend-icon=""
            :max-width="maxWidth"
            @update:model-value="handleDateChange"
        />

        <BChipGroup
            :model-value="selectedPreset"
            :items="chipItems"
            chip-size="x-small"
            class="preset-chips"
            @update:model-value="handlePresetChange"
        />
    </div>
</template>
